"use client";
import AuthenticationActionBar from "@components/auth/auth-action-bar/AuthenticationActionBar";
import { Link } from "@components/cms/link/Link";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { FooterEmailSubscription } from "@components/layouts/footer/footer-email-subscription/FooterEmailSubscription";
import { CountrySelector } from "@components/ui/country-selector/CountrySelector";
import { SocialMediaWidget } from "@components/ui/social-media-widget";
import { activeSiteHasLiveChat } from "@config/site/site-config";
import type { IFooter, ISiteSettings } from "@contentful-api/types/contentful";
import { useCountriesSelector } from "@hooks/useCountrySelect";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { clearCustomerServiceIcon } from "@lib/utils/clearCustomerServiceIcon";
import { Text } from "@ui/components/content/text/Text";
import { Divider } from "@ui/components/data-display/divider/Divider";
import { Button } from "@ui/components/forms/button/Button";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { isEmpty } from "lodash-es";
import dynamic from "next/dynamic";
import { styles } from "./styles";
import { useState } from "react";
import { useInView } from "react-intersection-observer";
import { useThemeMediaQuery } from "@ui/hooks/useThemeMediaQuery";
import { ButtonLinkModal } from "@components/cms/link/ButtonLinkModal";

const LiveChat = dynamic(() => import("@components/ui/live-chat/LiveChat"), { ssr: false });
const LiveChatScript = dynamic(() => import("@lib/live-chat/LiveChatScript"), { ssr: false });

export interface FooterProps {
    footerData: IFooter;
    newsletterPolicy?: ISiteSettings["fields"]["eccoNewsletter"];
    isProductPage?: boolean;
}
export const menuColumns = "menu-columns";
export const itemTitle = "item-title";
declare global {
    interface Window {
        CookieInformation: any;
    }
}

export const Footer = ({ footerData, newsletterPolicy, isProductPage = false }: FooterProps) => {
    const { get: getMicrocopy } = useMicrocopy();
    const customerService = clearCustomerServiceIcon(footerData?.fields?.customerService);
    const contactInformation = footerData?.fields?.contactInformation;
    const copyright = footerData?.fields?.copyright;
    const linkCollections = footerData?.fields?.linkCollections;
    const socialLinks = footerData?.fields?.socialLinks;
    const isSalesForceLiveChat = process.env.NEXT_PUBLIC_LIVE_CHAT_SOLUTION !== "D365";

    const [countries, currentCountryId] = useCountriesSelector();

    const [shouldDisplayLiveChat, setShouldDisplayLiveChat] = useState(false);
    const { xl } = useThemeMediaQuery();
    const clientIsDesktop = xl;

    const { ref: footerRef } = useInView({
        threshold: 0,
        rootMargin: `0% 0% 1px 0%`,
        trackVisibility: true,
        delay: 100,
        onChange: (inView) => {
            if (!isProductPage || !clientIsDesktop) {
                setShouldDisplayLiveChat(true);
            } else {
                setShouldDisplayLiveChat(inView);
            }
        },
    });

    const renewCookieLabel = getMicrocopy(CommonCMS.shared, CommonCMS.cookieRenew);

    return (
        <Box data-testid="footer" sx={styles.container} id="footer" ref={footerRef}>
            <FooterEmailSubscription newsletterPolicy={newsletterPolicy} />
            <Divider sx={styles.divider} data-testid="divider" />
            <footer>
                <Flex px={[4, 8, 8]}>
                    <Flex sx={styles.mainContent}>
                        <Box sx={styles.leftPart}>
                            {!isEmpty(contactInformation) && (
                                <Box data-testid="contact-text" sx={styles.contactInformation}>
                                    <RichText data={contactInformation} />
                                </Box>
                            )}
                            <Box data-testid="custom-service" sx={styles.customerService}>
                                <RichText data={customerService} />
                            </Box>
                        </Box>
                        {!isEmpty(linkCollections) &&
                            linkCollections?.map((link, index) => (
                                <Box key={link?.sys?.id} sx={styles.group}>
                                    <Text
                                        sx={styles.groupCapture}
                                        data-testid="footer-group-title"
                                        textTransform="uppercase"
                                    >
                                        {link?.fields?.title}
                                    </Text>
                                    {index + 1 === linkCollections.length && (
                                        <Button
                                            variant="textLink"
                                            h="auto"
                                            key={link?.sys?.id}
                                            data-testid="renewcookie"
                                            aria-label={renewCookieLabel}
                                            size="sm"
                                            sx={{ ...styles.link, lineHeight: "base" }}
                                            onClick={() => window.showCookieBanner()}
                                        >
                                            <span>{renewCookieLabel}</span>
                                        </Button>
                                    )}
                                    {link?.fields?.links?.map((link) =>
                                        link?.fields?.hasOwnProperty("modal") ? (
                                            <ButtonLinkModal
                                                key={link?.sys?.id}
                                                variant="textLink"
                                                link={link}
                                                size="sm"
                                            />
                                        ) : (
                                            <Link
                                                key={link?.sys?.id}
                                                link={link}
                                                data-testid={itemTitle}
                                                sx={styles.link}
                                            >
                                                {link.fields.label}
                                            </Link>
                                        )
                                    )}
                                </Box>
                            ))}
                    </Flex>
                </Flex>
                <Flex sx={styles.socialMediaContainer}>
                    <Box sx={styles.authContainer}>
                        <AuthenticationActionBar />
                    </Box>
                    <SocialMediaWidget socialLinks={socialLinks} />
                </Flex>
                <Divider sx={styles.divider} data-testid="divider" />
                <Flex
                    px={[4, 8, 8]}
                    py={[3, 0]}
                    gap={[0, 4]}
                    justifyContent="space-between"
                    flexDirection={["column-reverse", "row"]}
                    alignItems={["flex-start", "center"]}
                >
                    <Flex sx={styles.privacyLocationContainer} data-testid="privacy-location">
                        <Box sx={styles.privacy} data-testid="footer-copyright-text">
                            {copyright}
                        </Box>
                    </Flex>
                    <Flex sx={styles.privacyLocationContainer}>
                        <CountrySelector
                            currentLocationId={currentCountryId}
                            locationList={countries}
                        />
                    </Flex>
                </Flex>
                {activeSiteHasLiveChat && (
                    <>
                        <LiveChatScript shouldDisplayLiveChat={shouldDisplayLiveChat} />
                        {isSalesForceLiveChat && <LiveChat />}
                    </>
                )}
            </footer>
        </Box>
    );
};

export default Footer;
